import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  defaultEPlannerOffset,
  EPLANNER_OFFSET_UNITS,
  maxAmountForEPlannerUnit,
  nextEPlannerOffset,
  fetchOrganizationReminderSettings,
  getReminderSettingsSuccessMessage,
  parseOrganizationReminderSettings,
  saveOrganizationReminderSettings,
  validateOrganizationReminderSettingsForm,
} from "../../../services/orgReminderSettingsService";
import AppointmentTimePicker from "../eplanner/appointments/AppointmentTimePicker";
import "../eplanner/appointments/AppointmentTimePicker.scss";
import "../SuperAdmin/OrganizationSetup.scss";

const pad2 = (value) => String(value).padStart(2, "0");

const splitHHmm = (hhmm) => {
  const text = String(hhmm || "").trim();
  const match = text.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return { hour: "", minute: "", ampm: "AM" };
  const hour24 = Number(match[1]);
  const minute = Number(match[2]);
  if (Number.isNaN(hour24) || Number.isNaN(minute)) {
    return { hour: "", minute: "", ampm: "AM" };
  }
  const ampm = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return { hour: String(hour12), minute: pad2(minute), ampm };
};

const toHHmm = (hourRaw, minuteRaw, ampm) => {
  let hour = Number(hourRaw);
  const minute = Number(minuteRaw);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return "";
  if (hour < 1 || hour > 12 || minute < 0 || minute > 59) return "";
  if (ampm === "PM" && hour < 12) hour += 12;
  if (ampm === "AM" && hour === 12) hour = 0;
  return `${pad2(hour)}:${pad2(minute)}`;
};

const snapMinute = (raw) => {
  const minute = Number(raw);
  if (Number.isNaN(minute)) return "";
  const snapped = Math.min(55, Math.max(0, Math.round(minute / 5) * 5));
  return pad2(snapped);
};

const DailyRunTimeInput = ({ id, value, disabled, invalid, onChange }) => {
  const parts = splitHHmm(value);
  const [hour, setHour] = useState(parts.hour);
  const [minute, setMinute] = useState(parts.minute);
  const [ampm, setAmpm] = useState(parts.ampm || "AM");

  useEffect(() => {
    const next = splitHHmm(value);
    setHour(next.hour);
    setMinute(next.minute);
    setAmpm(next.ampm || "AM");
  }, [value]);

  const commit = (nextHour, nextMinute, nextAmpm) => {
    const normalized = toHHmm(nextHour, nextMinute, nextAmpm);
    if (normalized) onChange(normalized);
  };

  return (
    <div className="org-setup-time-picker-wrap">
    <AppointmentTimePicker
      value={value}
      onChange={onChange}
      disabled={disabled}
      intervalMinutes={5}
      ignorePast
      fullDay
      aria-label="Daily run time"
      renderTrigger={({ triggerRef, open, setOpen, disabled: pickerDisabled }) => (
        <div
          ref={triggerRef}
          className={`org-setup-time-segments${open ? " is-open" : ""}${
            invalid ? " is-invalid" : ""
          }`}
        >
          <input
            id={id}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            className="org-setup-time-segments__part"
            placeholder="--"
            maxLength={2}
            disabled={pickerDisabled}
            value={hour}
            aria-label="Hours"
            onChange={(e) => setHour(e.target.value.replace(/\D/g, "").slice(0, 2))}
            onBlur={() => {
              if (hour === "") return;
              const nextHour = String(Math.min(12, Math.max(1, Number(hour) || 12)));
              setHour(nextHour);
              commit(nextHour, minute || "00", ampm || "AM");
            }}
          />
          <span className="org-setup-time-segments__sep">:</span>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            className="org-setup-time-segments__part"
            placeholder="--"
            maxLength={2}
            disabled={pickerDisabled}
            value={minute}
            aria-label="Minutes"
            onChange={(e) => setMinute(e.target.value.replace(/\D/g, "").slice(0, 2))}
            onBlur={() => {
              if (minute === "") return;
              const nextMinute = snapMinute(minute);
              setMinute(nextMinute);
              commit(hour || "12", nextMinute, ampm || "AM");
            }}
          />
          <button
            type="button"
            className="org-setup-time-segments__ampm"
            disabled={pickerDisabled}
            aria-label="AM or PM"
            onClick={() => {
              const next = ampm === "PM" ? "AM" : "PM";
              setAmpm(next);
              commit(hour || "12", minute || "00", next);
            }}
          >
            {ampm || "AM"}
          </button>
          <button
            type="button"
            className="org-setup-time-segments__clock"
            disabled={pickerDisabled}
            aria-label="Open time picker"
            aria-expanded={open}
            onClick={() => {
              if (pickerDisabled) return;
              setOpen((current) => !current);
            }}
          >
            <i className="far fa-clock" aria-hidden="true" />
          </button>
        </div>
      )}
    />
    </div>
  );
};

const OrganizationRemindersSection = ({
  organizationId,
  editorStaffId,
  showAlert,
  isActive = true,
  organisationTimeZoneId = "",
  isSuperAdmin = false,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [form, setForm] = useState({
    remindersEnabled: false,
    reminderDailyRunTime: "09:00",
    allowMultipleEPlannerReminders: false,
    ePlannerOffsets: [defaultEPlannerOffset()],
    timeZoneId: "",
  });

  const showAlertRef = useRef(showAlert);
  showAlertRef.current = showAlert;

  const loadSettings = useCallback(async () => {
    if (!organizationId || !isActive) return;
    setLoading(true);
    try {
      const response = await fetchOrganizationReminderSettings(organizationId);
      const parsed = parseOrganizationReminderSettings(response);
      if (!parsed.ok) {
        showAlertRef.current({
          type: "error",
          title: "Unable to load reminder settings",
          message: parsed.message,
        });
        return;
      }
      setForm({
        remindersEnabled: parsed.remindersEnabled,
        reminderDailyRunTime: parsed.reminderDailyRunTime,
        allowMultipleEPlannerReminders: parsed.allowMultipleEPlannerReminders,
        ePlannerOffsets: parsed.allowMultipleEPlannerReminders
          ? parsed.ePlannerOffsets
          : (parsed.ePlannerOffsets || []).slice(0, 1),
        timeZoneId: parsed.timeZoneId,
      });
      setFieldErrors({});
    } catch (err) {
      showAlertRef.current({
        type: "error",
        title: "Unable to load reminder settings",
        message: err?.message || "Could not load reminder settings.",
      });
    } finally {
      setLoading(false);
    }
  }, [organizationId, isActive]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const updateField = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "allowMultipleEPlannerReminders" && !value) {
        next.ePlannerOffsets = (prev.ePlannerOffsets || []).slice(0, 1);
      }
      return next;
    });
    setFieldErrors((prev) => {
      if (!prev[key] && !prev.ePlannerOffsets) return prev;
      const next = { ...prev };
      delete next[key];
      if (key === "allowMultipleEPlannerReminders") delete next.ePlannerOffsets;
      return next;
    });
  };

  const updateOffset = (key, patch) => {
    setForm((prev) => ({
      ...prev,
      ePlannerOffsets: (prev.ePlannerOffsets || []).map((row) =>
        row.key === key ? { ...row, ...patch } : row
      ),
    }));
    setFieldErrors((prev) => {
      if (!prev.ePlannerOffsets) return prev;
      const next = { ...prev };
      delete next.ePlannerOffsets;
      return next;
    });
  };

  const addOffset = () => {
    setForm((prev) => {
      if (!prev.allowMultipleEPlannerReminders) return prev;
      return {
        ...prev,
        ePlannerOffsets: [...(prev.ePlannerOffsets || []), nextEPlannerOffset(prev.ePlannerOffsets)],
      };
    });
  };

  const handleAddReminderPoint = () => {
    if (saving) return;
    if (!form.allowMultipleEPlannerReminders) {
      showAlert?.({
        type: "warning",
        message:
          "This is not enabled for your organisation yet. Please ask the AQ team to turn on multiple reminder points.",
      });
      return;
    }
    if (offsets.length >= 12) {
      showAlert?.({
        type: "warning",
        message: "You can add up to 12 e-Planner reminder points.",
      });
      return;
    }
    addOffset();
  };

  const removeOffset = (key) => {
    setForm((prev) => {
      const next = (prev.ePlannerOffsets || []).filter((row) => row.key !== key);
      return {
        ...prev,
        ePlannerOffsets: next.length > 0 ? next : [defaultEPlannerOffset()],
      };
    });
  };

  const handleSave = async () => {
    const validation = validateOrganizationReminderSettingsForm(form);
    if (!validation.valid) {
      setFieldErrors({ [validation.field]: validation.message });
      if (validation.field !== "ePlannerOffsets") {
        showAlert({
          type: "error",
          title: validation.title,
          message: validation.message,
        });
      }
      return;
    }

    setSaving(true);
    try {
      const runTime = String(form.reminderDailyRunTime || "09:00").trim();
      const parts = runTime.split(":");
      const normalizedTime = `${String(parts[0] || "09").padStart(2, "0")}:${String(parts[1] || "00").padStart(2, "0")}:00`;
      const offsets = (form.ePlannerOffsets || []).map((row, index) => ({
        Amount: Number(row.amount),
        Unit: row.unit,
        SortOrder: index + 1,
      }));
      const primaryOffset = offsets[0];
      const ePlannerDaysBefore =
        primaryOffset?.Unit === "Week"
          ? Number(primaryOffset.Amount) * 7
          : primaryOffset?.Unit === "Month"
            ? Number(primaryOffset.Amount) * 30
            : Number(primaryOffset?.Amount) || 1;

      const response = await saveOrganizationReminderSettings({
        OrganizationId: organizationId,
        UpdatedBy: editorStaffId,
        RemindersEnabled: Boolean(form.remindersEnabled),
        ReminderDailyRunTime: normalizedTime,
        EPlannerDaysBefore: ePlannerDaysBefore,
        AllowMultipleEPlannerReminders: isSuperAdmin
          ? Boolean(form.allowMultipleEPlannerReminders)
          : Boolean(form.allowMultipleEPlannerReminders),
        EPlannerOffsets: form.allowMultipleEPlannerReminders ? offsets : offsets.slice(0, 1),
        ReminderEmailEnabled: true,
        ReminderSmsEnabled: false,
        ReminderAppEnabled: false,
      });

      if (Number(response?.success) !== 1) {
        const saveMessage = response?.message || "Could not save reminder settings.";
        const isOffsetError = /reminder point|already exists|1 year/i.test(saveMessage);
        if (isOffsetError) {
          setFieldErrors({ ePlannerOffsets: saveMessage });
        } else {
          showAlert({
            type: "error",
            title: "Could not save",
            message: saveMessage,
          });
        }
        return;
      }

      showAlert({
        type: "success",
        title: "Saved",
        message: response?.message || getReminderSettingsSuccessMessage(),
      });
      await loadSettings();
    } catch (err) {
      showAlert({
        type: "error",
        title: "Could not save",
        message: err?.message || "Could not save reminder settings.",
      });
    } finally {
      setSaving(false);
    }
  };

  const tzLabel = organisationTimeZoneId || form.timeZoneId || "GMT Standard Time";
  const offsets = form.ePlannerOffsets || [];

  if (loading) {
    return <div className="text-muted">Loading reminder settings...</div>;
  }

  return (
    <div>
      <h6 className="mb-1">Reminders</h6>
      <p className="text-muted small mb-3">
        Master on/off and daily run time for automatic reminders.
        Send rules stay on each module&apos;s own setup.
      </p>
      <div className="row g-3">
        <div className="col-12">
          <div className="org-setup-reminder-run">
            <div className="form-check form-switch mb-0 org-setup-reminder-run__toggle">
              <input
                className="form-check-input"
                type="checkbox"
                id="remindersEnabled"
                checked={Boolean(form.remindersEnabled)}
                disabled={saving}
                onChange={(e) => updateField("remindersEnabled", e.target.checked)}
              />
              <label className="form-check-label" htmlFor="remindersEnabled">
                Reminders enabled
              </label>
            </div>
            <label className="form-label mb-0 org-setup-reminder-run__label" htmlFor="reminderDailyRunTime">
              Daily run time
            </label>
            <div className="org-setup-reminder-run__picker">
              <DailyRunTimeInput
                id="reminderDailyRunTime"
                value={form.reminderDailyRunTime || ""}
                disabled={saving}
                invalid={Boolean(fieldErrors.reminderDailyRunTime)}
                onChange={(next) => updateField("reminderDailyRunTime", next)}
              />
            </div>
            {fieldErrors.reminderDailyRunTime ? (
              <div className="invalid-feedback d-block org-setup-reminder-run__hint">
                {fieldErrors.reminderDailyRunTime}
              </div>
            ) : (
              <div className="small text-muted org-setup-reminder-run__hint">
                Times use organisation timezone: <strong>{tzLabel}</strong>
              </div>
            )}
          </div>
        </div>

        {isSuperAdmin ? (
          <div className="col-12">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="allowMultipleEPlannerReminders"
                checked={Boolean(form.allowMultipleEPlannerReminders)}
                disabled={saving}
                onChange={(e) => updateField("allowMultipleEPlannerReminders", e.target.checked)}
              />
              <label className="form-check-label" htmlFor="allowMultipleEPlannerReminders">
                Allow multiple reminder points
              </label>
            </div>
            <div className="form-text">
              Super Admin only. Turn this on for the organisation to enable Add reminder point.
              When off, this organisation uses a single ePlanner reminder.
            </div>
          </div>
        ) : null}

        <div className="col-12">
          <div className="org-setup-eplanner-points">
            <label className="form-label d-block">e-Planner Reminder Point</label>
            <p className="form-text mt-0 mb-3">
              Send a reminder this long before the appointment (organisation-local date).
              {form.allowMultipleEPlannerReminders
                ? " Add more than one point if the same visit should be reminded at different times."
                : " This organisation is limited to one reminder point."}
            </p>
            {offsets.map((row, index) => (
              <div className="mb-2" key={row.key}>
                <label className="form-label mb-1" htmlFor={`eplanner-offset-amount-${row.key}`}>
                  Before
                </label>
                <div className="org-setup-eplanner-points__fields">
                  <input
                    id={`eplanner-offset-amount-${row.key}`}
                    type="number"
                    min={1}
                    max={maxAmountForEPlannerUnit(row.unit)}
                    className={`form-control org-setup-eplanner-points__amount ${
                      fieldErrors.ePlannerOffsets ? "is-invalid" : ""
                    }`}
                    style={{ width: "5.5rem" }}
                    value={row.amount}
                    disabled={saving}
                    aria-label={`Reminder point ${index + 1} amount`}
                    onChange={(e) => updateOffset(row.key, { amount: e.target.value })}
                  />
                  <select
                    className="form-control org-setup-eplanner-points__unit"
                    style={{ width: "8.5rem" }}
                    value={row.unit}
                    disabled={saving}
                    aria-label={`Reminder point ${index + 1} unit`}
                    onChange={(e) => updateOffset(row.key, { unit: e.target.value })}
                  >
                    {EPLANNER_OFFSET_UNITS.map((unit) => (
                      <option key={unit} value={unit}>
                        {unit === "Day" ? "Day(s)" : unit === "Week" ? "Week(s)" : "Month(s)"}
                      </option>
                    ))}
                  </select>
                  {form.allowMultipleEPlannerReminders && offsets.length > 1 ? (
                    <button
                      type="button"
                      className="btn btn-outline-secondary org-setup-eplanner-points__action"
                      disabled={saving}
                      onClick={() => removeOffset(row.key)}
                    >
                      Remove
                    </button>
                  ) : null}
                  {index === offsets.length - 1 ? (
                    <button
                      type="button"
                      className="btn btn-outline-primary org-setup-eplanner-points__action"
                      disabled={saving}
                      title={
                        form.allowMultipleEPlannerReminders
                          ? "Add another day, week, or month reminder point"
                          : "Find out when this action is enabled"
                      }
                      onClick={handleAddReminderPoint}
                    >
                      Add more reminder point
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
            {fieldErrors.ePlannerOffsets ? (
              <div className="text-danger small">{fieldErrors.ePlannerOffsets}</div>
            ) : null}
          </div>
        </div>

      </div>

      <div className="mt-4 d-flex justify-content-end">
        <button
          type="button"
          className="btn btn-primary"
          disabled={saving}
          onClick={handleSave}
        >
          {saving ? "Saving..." : "Save reminder settings"}
        </button>
      </div>
    </div>
  );
};

export default OrganizationRemindersSection;
